import React from "react";
import { roleConfig, canAccessPage } from "../config/roleConfig";
import {
  SIDEBAR_ITEMS,
  FLAT_LAYOUT_ORDER,
  SECTIONED_LAYOUT,
  CATEGORIES,
  CATEGORY_ORDER,
} from "../config/sidebarItems";
import type { CategoryKey } from "../config/sidebarItems";
import { NavIcons } from "../components/ui/Icons";
import type { UserRole } from "../types/core";

const CATEGORY_META = Object.fromEntries(CATEGORIES.map((c) => [c.key, c])) as Record<
  CategoryKey,
  (typeof CATEGORIES)[number]
>;

// =============================================================================
// SIDEBAR VIEWER — Dev Tools page
// =============================================================================
// PURPOSE: Render every role's sidebar in a single grid so you can audit RBAC
// without cycling through "View As" for every team member.
//
// PREVIEW-ONLY: NavItems are <div>, not <NavLink>. Clicks do nothing. This is
// purely a visual snapshot — no auth, no presence, no realtime. The 7-instance
// trap (mounting <Sidebar/> seven times) would multiply useAuth/usePresence
// channels and break everything; this page sidesteps that by re-rendering the
// nav structure from data only.
//
// LAYOUT PARITY (Jul 13, 2026): the preview mirrors the real Sidebar's two
// modes — exec/manager get a category RAIL (icon + label + count + chevron per
// category; hover-flyout on desktop, shown expanded here); reps/client get a
// flat list sub-grouped by category label. Header components below mirror the
// real <CategoryHeader> and <SubGroupLabel> in components/Sidebar.tsx.
// =============================================================================

// KEEP IN SYNC with the UserRole union in `types/core.ts`. TypeScript can't
// enforce exhaustiveness on a plain array literal — the runtime assertion
// below catches the drift if a new role is added but forgotten here.
const ROLE_ORDER: UserRole[] = [
  "exec",
  "manager",
  "client",
  "closer",
  "setter",
  "dialer",
  "viewer",
];

// SSOT for tunnel-vision roles in this preview. Mirrors `useRBAC.hasTunnelVision`
// but doesn't go through useRBAC because this page has no auth dependency.
// If a new tunnel-vision role is added, update both this AND useRBAC.ts.
const TUNNEL_VISION_ROLES: UserRole[] = ["closer", "setter", "dialer"];
const isTunnelVisionRole = (role: UserRole) => TUNNEL_VISION_ROLES.includes(role);

const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  exec: "Full god mode — every page",
  manager: "Pod oversight, team analytics",
  client: "External client, outward intelligence",
  closer: "Cash focus, tunnel vision",
  setter: "DM quality, only non-exec with DM Analytics",
  dialer: "Call volume, tunnel vision",
  viewer: "Deny-all fallback for unknown roles",
};

interface PreviewNavItemProps {
  iconKey: keyof typeof NavIcons;
  label: string;
}

const PreviewNavItem: React.FC<PreviewNavItemProps> = ({ iconKey, label }) => {
  const Icon = NavIcons[iconKey];
  return (
    <div className="sidebar-nav-item token-calendar flex items-center gap-2 px-2 py-1.5 rounded-lg text-glass-secondary">
      <span className="flex-shrink-0 text-current opacity-70">
        <Icon />
      </span>
      <span className="truncate">{label}</span>
    </div>
  );
};

// Category header row — mirrors the REAL desktop Sidebar's <ExecCategoryNav>
// rail row that exec/manager actually see: icon + label + page count + a RIGHT
// chevron (the desktop rail uses ChevronRight to signal "hover to float the
// pages out to the right", Sidebar.tsx). This static preview keeps that right
// chevron for visual parity, then renders the category's pages expanded below
// it so every page stays auditable (a real hover-flyout can't be shown in a
// static grid). NOTE: count = every page in this card, DEV PAGES INCLUDED — the
// Sidebar Viewer is itself a devMode-only page, so any viewer already has
// devMode on and their real rail counts the dev pages too (matches Mako's own
// devMode-on rail; the dimmed rows below are those dev pages).
const PreviewCategoryHeader: React.FC<{
  iconKey: keyof typeof NavIcons;
  label: string;
  count: number;
}> = ({ iconKey, label, count }) => {
  const Icon = NavIcons[iconKey];
  return (
    <div className="sidebar-nav-item token-calendar w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-glass-secondary">
      <span className="flex-shrink-0 text-current opacity-70">
        <Icon />
      </span>
      <span className="truncate flex-1 font-medium">{label}</span>
      <span className="token-9 text-glass-muted tabular-nums flex-shrink-0">{count}</span>
      <span className="flex-shrink-0 text-glass-muted">
        <NavIcons.ChevronRight />
      </span>
    </div>
  );
};

// Sub-group label — mirrors the REAL Sidebar's <SubGroupLabel> for the flat
// (rep/client) layout: smaller + tighter than a category header because it sits
// inside one flat list, not a collapsible rail.
const PreviewSubGroupLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="px-2 pt-2.5 pb-1 token-9 text-glass-muted uppercase tracking-wide">
    {children}
  </div>
);

// Visible fallback when a path is in roleConfig but missing from SIDEBAR_ITEMS.
// Without this, the viewer's whole point — catching RBAC drift — would itself
// silently swallow the very drift it's meant to surface.
const MissingMetaRow: React.FC<{ path: string }> = ({ path }) => (
  <div className="sidebar-nav-item token-calendar flex items-center gap-2 px-2 py-1.5 rounded-lg text-ds-amber border border-ds-amber/30 bg-ds-amber/5">
    <span className="truncate" title={`Missing from config/sidebarItems.ts: ${path}`}>
      ⚠ {path}
    </span>
  </div>
);

// Runtime exhaustiveness check — `ROLE_ORDER` is a plain array so TS can't
// guarantee every UserRole appears. This compares against ROLE_DESCRIPTIONS
// (which IS exhaustive via Record<UserRole, string>) and warns in dev.
const checkRoleOrderExhaustive = (order: UserRole[], descriptions: Record<UserRole, string>) => {
  if (!import.meta.env.DEV) return;
  const allRoles = Object.keys(descriptions) as UserRole[];
  const missing = allRoles.filter((r) => !order.includes(r));
  if (missing.length > 0) {
    console.warn(
      `[SidebarViewer] ROLE_ORDER missing roles: ${missing.join(", ")} — update SidebarViewerPage.tsx`
    );
  }
};

interface RolePreviewProps {
  role: UserRole;
}

const RolePreview: React.FC<RolePreviewProps> = ({ role }) => {
  const view = roleConfig[role];
  const hasPage = (path: string) => canAccessPage(role, path);
  const hasSection = (s: string) => view.sidebar.includes(s as any);

  const hasTunnelVision = isTunnelVisionRole(role);
  const isClient = role === "client";
  const showSections = !hasTunnelVision && !isClient;

  // Empty state for viewer (pages = [])
  if (view.pages.length === 0) {
    return (
      <div className="px-2 py-6 text-center">
        <p className="token-10 text-glass-muted leading-relaxed">
          No pages.
          <br />
          Deny-all fallback.
        </p>
      </div>
    );
  }

  // Same source + filter the real Sidebar uses, minus the devMode filter (the
  // viewer SHOWS dev pages, just dimmed, so RBAC drift is always auditable).
  const source = showSections
    ? [...SECTIONED_LAYOUT.dashboards, ...SECTIONED_LAYOUT.management, ...SECTIONED_LAYOUT.devTools]
    : FLAT_LAYOUT_ORDER;

  const visible = source.filter((path) => {
    if (!hasPage(path)) return false;
    if (path === "/admin/portal" && !showSections && hasSection("execTools")) return false;
    if (path === "/admin/portal" && showSections && !hasSection("execTools")) return false;
    return true;
  });

  // Group by category (same taxonomy the real sidebar renders).
  const byCat = new Map<CategoryKey, string[]>();
  const missing: string[] = [];
  for (const path of visible) {
    const meta = SIDEBAR_ITEMS[path];
    if (!meta) {
      if (import.meta.env.DEV) {
        console.warn(
          `[SidebarViewer] Path "${path}" missing from SIDEBAR_ITEMS — add it to config/sidebarItems.ts`
        );
      }
      missing.push(path);
      continue;
    }
    if (!byCat.has(meta.category)) byCat.set(meta.category, []);
    byCat.get(meta.category)!.push(path);
  }

  const labelFor = (path: string): string => {
    const meta = SIDEBAR_ITEMS[path];
    if (!meta) return path;
    if (path === "/admin/rep-portal" && hasTunnelVision) {
      return `${role.charAt(0).toUpperCase() + role.slice(1)}'s Portal`;
    }
    if (path === "/admin/portal" && !showSections) return "Portal";
    return meta.label;
  };

  return (
    <>
      {CATEGORY_ORDER.map((cat) => {
        const paths = byCat.get(cat);
        if (!paths || paths.length === 0) return null;
        const catMeta = CATEGORY_META[cat];
        return (
          <div key={cat}>
            {/* Exec/manager render a category RAIL row (icon + count + chevron);
                reps/client render a flat sub-group label — matching the real
                Sidebar's two layouts exactly. */}
            {showSections ? (
              <PreviewCategoryHeader
                iconKey={catMeta.iconKey}
                label={catMeta.label}
                count={paths.length}
              />
            ) : (
              <PreviewSubGroupLabel>{catMeta.label}</PreviewSubGroupLabel>
            )}
            <div className={showSections ? "space-y-0.5 mt-0.5 mb-1 pl-1" : "space-y-0.5"}>
              {paths.map((path) => {
                const meta = SIDEBAR_ITEMS[path]!;
                // devOnly items only render in the real sidebar under devMode —
                // dim them here so the preview signals "hidden unless devMode".
                return (
                  <div key={path} className={meta.devOnly ? "opacity-60" : ""}>
                    <PreviewNavItem iconKey={meta.iconKey} label={labelFor(path)} />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {missing.map((path) => (
        <MissingMetaRow key={path} path={path} />
      ))}
    </>
  );
};

const SidebarViewerPage: React.FC = () => {
  checkRoleOrderExhaustive(ROLE_ORDER, ROLE_DESCRIPTIONS);

  return (
    <div className="space-y-6">
      <header className="liquid-glass-hero p-6">
        <h1 className="token-38 font-bold text-glass mb-2">Sidebar Viewer</h1>
        <p className="text-glass-secondary token-calendar leading-relaxed max-w-3xl">
          Every role's sidebar, side by side. Preview only — clicks do nothing. Page-access source
          is{" "}
          <code className="px-1.5 py-0.5 rounded bg-black/[0.04] token-13m">
            config/roleConfig.ts
          </code>
          ; labels + icons + order come from{" "}
          <code className="px-1.5 py-0.5 rounded bg-black/[0.04] token-13m">
            config/sidebarItems.ts
          </code>{" "}
          — the same SSOT consumed by <code className="token-13m">components/Sidebar.tsx</code>, so
          this preview stays real-time with the actual sidebar (edit one file, both surfaces
          update). <strong className="text-glass">Exec &amp; manager</strong> see a category rail —
          each row (Home / Deals / Analytics / Comms / Directory / Admin) shows a page count and, on
          desktop, floats its pages out in a hover flyout; this preview renders every category
          expanded so all pages stay auditable.{" "}
          <strong className="text-glass">Reps &amp; client</strong> get a flat list sub-grouped by
          those same category labels. Dimmed items are dev-only — they render in the real sidebar
          only when devMode is on. Amber rows = a path in roleConfig but missing from
          sidebarItems.ts (RBAC drift).
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-7 gap-4">
        {ROLE_ORDER.map((role) => {
          const view = roleConfig[role];
          const hasTunnelVision = isTunnelVisionRole(role);
          const isClient = role === "client";
          const layoutLabel =
            view.pages.length === 0
              ? "—"
              : !hasTunnelVision && !isClient
                ? "category rail"
                : "flat";

          return (
            <div
              key={role}
              className="liquid-glass anim-lift flex flex-col"
              style={{ minHeight: 480 }}
            >
              {/* Header */}
              <div className="px-3 py-3 border-b border-black/[0.08]">
                <div className="flex items-baseline justify-between mb-1">
                  <h2 className="token-15 font-semibold text-glass capitalize">{role}</h2>
                  <span className="token-10 text-glass-muted">
                    {view.pages.length} {view.pages.length === 1 ? "page" : "pages"}
                  </span>
                </div>
                <p className="token-10 text-glass-muted leading-snug">{ROLE_DESCRIPTIONS[role]}</p>
                <div className="mt-1.5 flex items-center gap-1.5">
                  <span className="token-9 uppercase text-glass-muted tracking-wider">layout:</span>
                  <span className="token-9 px-1.5 py-0.5 rounded bg-black/[0.04] text-glass-secondary">
                    {layoutLabel}
                  </span>
                </div>
              </div>

              {/* Nav body */}
              <div className="flex-1 overflow-y-auto py-1 px-1.5">
                <RolePreview role={role} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SidebarViewerPage;
